import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cake, ChevronLeft, ChevronRight, Gift } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { clientApi } from '@/lib/adminApi';

// Founder UAT 2026-06 — compact monthly birthday calendar for TalentHeart.
// Consent-aware (server only returns authorized birthdays). Month navigation,
// highlighted birthday dates, click a date to identify the employee(s).

const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DOW_ES = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const DOW_EN = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export default function BirthdayCalendarCard({ emptyStateText }) {
  const { t, i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    setLoading(true);
    setSelectedDay(null);
    clientApi.listTalentHeartBirthdaysByMonth(month)
      .then((d) => setRows(Array.isArray(d) ? d : (d?.rows || [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [month]);

  const byDay = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      if (!m.has(r.day)) m.set(r.day, []);
      m.get(r.day).push(r);
    });
    return m;
  }, [rows]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDow = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Monday-first
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const nav = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const isToday = (d) =>
    d === now.getDate() && month === now.getMonth() + 1 && year === now.getFullYear();

  const monthName = (isES ? MONTHS_ES : MONTHS_EN)[month - 1];
  const dow = isES ? DOW_ES : DOW_EN;

  return (
    <Card className="p-5 shadow-card overflow-hidden relative" data-testid="theart-birthday-calendar">
      <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-pink-500 via-orange-400 to-amber-400" aria-hidden="true" />
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold inline-flex items-center gap-2">
          <Cake className="h-4 w-4 text-pink-500" />
          {t('clientTalentHeart.birthdays.title')}
        </h2>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => nav(-1)} data-testid="bday-cal-prev"
                  className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground"
                  aria-label={isES ? 'Mes anterior' : 'Previous month'}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium min-w-[110px] text-center tabular-nums" data-testid="bday-cal-month">
            {monthName} {year}
          </span>
          <button type="button" onClick={() => nav(1)} data-testid="bday-cal-next"
                  className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground"
                  aria-label={isES ? 'Mes siguiente' : 'Next month'}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center mb-1">
        {dow.map((d, i) => (
          <span key={i} className="text-[10px] uppercase font-semibold text-muted-foreground">{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1" data-testid="bday-cal-grid">
        {cells.map((d, i) => {
          if (d === null) return <span key={`e${i}`} />;
          const people = byDay.get(d) || [];
          const has = people.length > 0;
          return (
            <button
              key={d}
              type="button"
              disabled={!has}
              onClick={() => setSelectedDay(selectedDay === d ? null : d)}
              title={has ? people.map((p) => p.name).join(', ') : undefined}
              data-testid={has ? `bday-cal-day-${d}` : undefined}
              className={`relative h-8 rounded-md text-xs tabular-nums flex items-center justify-center transition-colors
                ${has
                  ? 'bg-gradient-to-br from-pink-500 to-orange-400 text-white font-bold shadow-sm hover:opacity-90 cursor-pointer'
                  : isToday(d)
                    ? 'ring-1 ring-blue-400 text-blue-600 font-semibold'
                    : 'text-foreground/70'}
                ${selectedDay === d ? 'ring-2 ring-pink-500 ring-offset-1' : ''}`}
            >
              {d}
              {has && <Gift className="h-2.5 w-2.5 absolute -top-1 -right-1 text-amber-500 fill-amber-200" />}
            </button>
          );
        })}
      </div>

      {loading ? null : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground mt-3" data-testid="bday-cal-empty">
          {emptyStateText || t('clientTalentHeart.birthdayStates.nonePeriod')}
        </p>
      ) : (
        <ul className="mt-3 space-y-1.5 max-h-36 overflow-auto" data-testid="bday-cal-list">
          {(selectedDay ? (byDay.get(selectedDay) || []) : rows).map((r, i) => (
            <li key={`${r.user_id}-${i}`} className="flex items-center gap-2 text-sm" data-testid={`bday-cal-person-${i}`}>
              <Gift className="h-3.5 w-3.5 text-pink-500 shrink-0" />
              <span className="flex-1 truncate font-medium">{r.name}</span>
              {r.position && <span className="text-xs text-muted-foreground truncate max-w-[100px]">{r.position}</span>}
              <span className="text-xs font-semibold text-orange-600 tabular-nums shrink-0">{r.day} {monthName.slice(0, 3)}</span>
            </li>
          ))}
        </ul>
      )}
      {selectedDay && (
        <button type="button" onClick={() => setSelectedDay(null)} data-testid="bday-cal-clear"
                className="text-xs text-primary hover:underline mt-2">
          {isES ? 'Ver todo el mes' : 'Show full month'}
        </button>
      )}
    </Card>
  );
}
